import type { Channel, Me, UserSummary } from '@novachat/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SettingsDialog } from '../components/SettingsDialog';
import { CreateJiraIssueDialog } from '../components/CreateJiraIssueDialog';
import { JiraIssueDialog } from '../components/JiraIssueDialog';
import { JiraPage, useJiraPage } from '../components/JiraPage';
import { Sidebar, useChannels } from '../components/Sidebar';
import { client } from '../lib/client';
import { desktop } from '../lib/desktop';
import { notifyAbout } from '../lib/notify';
import { applyChatEvent, chatReady, flushOutbox, forgetChannel, jumpToMessage, startChat, useChat } from '../state/chat';
import { applyEvent, setRealtime, useLive } from '../state/live';
import { openThread, useThreadPanel } from '../state/thread';
import { startNavigation } from '../state/nav';
import { reportActivity, reportFocus, resendPresence } from '../state/presence';
import { useVoice } from '../state/voice';
import { ConnectionNotice } from '../components/ConnectionNotice';
import { ThreadsPage } from '../components/Threads';
import { OngoingCall } from '../components/OngoingCall';
import { PopoutCall } from '../components/PopoutCall';
import { ProfileCard } from '../components/ProfileCard';
import { ScreenPicker } from '../components/ScreenPicker';
import { SearchDialog, useSearch } from '../components/SearchDialog';
import { ChannelPage } from './ChannelPage';
import { Home } from './Home';

const IDLE_AFTER_MS = 10 * 60_000;

/** `connect={false}` is for the development preview, which has no session to open a socket with. */
export function Shell({ user, connect = true }: { user: Me; connect?: boolean }) {
  useRealtime(connect, user);
  useUnreadTitle();
  useIdlePresence();
  useMicShortcut();
  useEffect(() => {
    if (connect) startChat(user.id);
  }, [connect, user.id]);
  useEffect(() => startNavigation(), []);
  const selected = useLive((s) => s.selectedChannelId);
  const threadsPage = useThreadPanel((s) => s.page);
  const jiraPage = useJiraPage((s) => s.shown);
  const { data: channels } = useChannels();
  const channel = channels?.find((c) => c.id === selected);

  return (
    // Phones show one panel at a time: the room list, or the open room with a back button.
    <div className="flex h-full min-h-0 bg-surface-1">
      <Sidebar user={user} className={selected || threadsPage || jiraPage ? 'max-md:hidden' : ''} />
      <main className={`relative flex min-h-0 min-w-0 flex-1 flex-col ${selected || threadsPage || jiraPage ? '' : 'max-md:hidden'}`}>
        <OngoingCall me={user} />
        <ConnectionNotice />
        {jiraPage && !selected ? <JiraPage /> : threadsPage && !selected ? <ThreadsPage /> : !selected ? <Home /> : channel ? <ChannelPage key={selected} channel={channel} me={user} /> : null}
      </main>
      <SettingsDialog />
      <CreateJiraIssueDialog />
      <JiraIssueDialog />
      <SearchDialog />
      <ProfileCard me={user} />
      <PopoutCall />
      {desktop?.setBadge && <DesktopBadge />}
      {desktop?.setCall && <DesktopCall />}
      {desktop?.onScreenPicker && <ScreenPicker />}
    </div>
  );
}

/** The desktop app follows the call (global shortcuts, menu bar mic) and passes its shortcuts back. */
function DesktopCall() {
  const inCall = useVoice((s) => s.room !== null);
  const micOn = useVoice((s) => s.micOn);
  const deafened = useVoice((s) => s.deafened);
  useEffect(() => {
    desktop?.setCall?.(inCall ? { micOn, deafened } : null);
  }, [inCall, micOn, deafened]);
  // Signing out ends the call.
  useEffect(() => () => desktop?.setCall?.(null), []);
  useEffect(
    () =>
      desktop?.onCallAction?.((action) => {
        const voice = useVoice.getState();
        if (!voice.room) return;
        if (action === 'mic') void voice.toggleMic();
        else voice.toggleDeafen();
      }),
    [],
  );
  return null;
}

/** The desktop app's dock/taskbar number: messages that would notify you, by each channel's level. */
function DesktopBadge() {
  const { data: channels } = useChannels();
  const reads = useChat((s) => s.reads);
  const count = (channels ?? []).reduce((sum, channel) => {
    const read = reads[channel.id];
    if (!read) return sum;
    return sum + (channel.notifyLevel === 'all' ? read.unread : channel.notifyLevel === 'mentions' ? read.mentions : 0);
  }, 0);
  useEffect(() => desktop?.setBadge?.(count), [count]);
  // Signing out leaves nothing waiting.
  useEffect(() => () => desktop?.setBadge?.(0), []);
  return null;
}

/** One socket per signed-in tab; reconnects by itself and replays a full snapshot on `ready`. */
function useRealtime(enabled: boolean, user: Me) {
  const queries = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const realtime = client.realtime({
      onEvent: (event) => {
        applyEvent(event, queries, (channelId) => {
          if (useLive.getState().selectedChannelId === channelId) useLive.getState().select(null);
          if (useVoice.getState().channelId === channelId) void useVoice.getState().leave();
          forgetChannel(channelId);
        });
        applyChatEvent(event);
        // "Görüşme başladı" and the like never notify.
        if (event.type === 'message.created' && event.message.kind !== 'system') {
          const { message } = event;
          const live = useLive.getState();
          const channel = queries.getQueryData<Channel[]>(['channels'])?.find((c) => c.id === message.channelId);
          const rootId = message.threadRootId;
          const focused = document.visibilityState === 'visible' && document.hasFocus();
          // A thread reply notifies whoever follows the thread (or is mentioned there), unless the channel is muted.
          const followsIt = !!rootId && (!!useChat.getState().followed[rootId] || message.mentions.userIds.includes(user.id));
          if (!rootId || followsIt) {
            notifyAbout(
              message,
              rootId && channel && channel.notifyLevel !== 'mute' ? { ...channel, notifyLevel: 'all' } : channel,
              user,
              {
                dnd: live.presence[user.id] === 'dnd',
                viewing: rootId ? useThreadPanel.getState().open?.rootId === rootId && focused : live.selectedChannelId === message.channelId && focused,
                people: queries.getQueryData<UserSummary[]>(['users']) ?? [],
              },
              () => {
                if (rootId) return openThread(message.channelId, rootId);
                useLive.getState().select(message.channelId);
                jumpToMessage(message.channelId, message.id);
              },
            );
          }
        }
        if (event.type === 'message.updated' || event.type === 'message.deleted') {
          const channelId = event.type === 'message.updated' ? event.message.channelId : event.channelId;
          void queries.invalidateQueries({ queryKey: ['pins', channelId] });
        }
        if (event.type === 'ready') {
          // Our own status preference survives reconnects; missed messages are fetched again.
          resendPresence();
          chatReady();
        }
      },
      onStatus: (status) => useLive.setState({ status }),
    });
    setRealtime(realtime);
    realtime.start();
    const online = () => {
      realtime.reconnectNow();
      void flushOutbox();
    };
    window.addEventListener('online', online);
    return () => {
      window.removeEventListener('online', online);
      setRealtime(null);
      realtime.stop();
      void useVoice.getState().leave();
    };
  }, [queries, enabled, user]);
}

/** "(3) NovaChat" while anything is unread, in the tab and the taskbar. */
function useUnreadTitle() {
  useEffect(() => {
    const update = (reads: ReturnType<typeof useChat.getState>['reads']) => {
      const count = Object.values(reads).reduce((sum, read) => sum + read.unread, 0);
      document.title = count > 0 ? `(${count > 99 ? '99+' : count}) NovaChat` : 'NovaChat';
    };
    update(useChat.getState().reads);
    return useChat.subscribe((state, previous) => {
      if (state.reads !== previous.reads) update(state.reads);
    });
  }, []);
}

function useIdlePresence() {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const activity = () => {
      clearTimeout(timer);
      reportActivity(true);
      timer = setTimeout(() => reportActivity(false), IDLE_AFTER_MS);
    };
    const events = ['pointerdown', 'keydown', 'pointermove', 'focus'] as const;
    for (const event of events) window.addEventListener(event, activity, { passive: true });
    activity();
    // In front or not: decides whether the phone gets push now (presence above only turns idle after a while).
    const focus = () => reportFocus(document.visibilityState === 'visible' && document.hasFocus());
    window.addEventListener('focus', focus);
    window.addEventListener('blur', focus);
    document.addEventListener('visibilitychange', focus);
    focus();
    return () => {
      clearTimeout(timer);
      for (const event of events) window.removeEventListener(event, activity);
      window.removeEventListener('focus', focus);
      window.removeEventListener('blur', focus);
      document.removeEventListener('visibilitychange', focus);
    };
  }, []);
}

/** ⌥M / Alt+M toggles the microphone while the window has focus (the desktop app adds global shortcuts). */
function useMicShortcut() {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // ⌘K / Ctrl+K: search
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        useSearch.getState().show();
        return;
      }
      if (event.altKey && !event.metaKey && !event.ctrlKey && event.code === 'KeyM' && useVoice.getState().room) {
        event.preventDefault();
        void useVoice.getState().toggleMic();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
